import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';
import { CampoContaMetadata, CAMPOS_CONTA } from './contas-campos';
import { GerarCobrancaPixComponent } from '../gerar-cobranca-pix/gerar-cobranca-pix.component';

interface ContaBancaria {
  codigoInterno: string;
  conta: string;
  descricao: string;
  codigoBanco: string;
  banco: string;
  codigoAgencia: string;
  agencia: string;
  codigoEmpresa: string;
  empresa: string;
  codigoPlanoContas: string;
  planoContas: string;
  tipoConta: string;
  ativa: string;
  saldoBanco: string;
  saldoReal: string;
  implantacao: string;
  chavePix: string;
  imprimeOrcamento: string;
}

type ColunaOrdenacao = 'codigoInterno' | 'conta' | 'descricao' | 'banco' | 'agencia' | 'empresa' | 'saldoReal' | 'ativa';
type AbaConta = 'geral' | 'cadastros' | 'pix' | 'extrato' | 'edi' | 'boletos' | 'saldos';

interface ErroModal {
  titulo: string;
  mensagem: string;
  codigo?: string;
}

interface AbaConfig { id: AbaConta; titulo: string; }

@Component({
  selector: 'app-contas',
  standalone: true,
  imports: [CommonModule, FormsModule, LoadingIndicatorComponent, GerarCobrancaPixComponent],
  templateUrl: './contas.component.html',
  styleUrl: './contas.component.css'
})
export class ContasComponent implements OnInit {
  readonly metadados = CAMPOS_CONTA;
  readonly abas: AbaConfig[] = [
    { id: 'geral', titulo: 'Geral' },
    { id: 'cadastros', titulo: 'Cadastros' },
    { id: 'pix', titulo: 'Pix' },
    { id: 'extrato', titulo: 'Cat. Lanc. Extrato Bancário' },
    { id: 'edi', titulo: 'Intercâmbio Eletrônico de Dados (EDI)' },
    { id: 'boletos', titulo: 'Boleto(s)/Duplicatas' },
    { id: 'saldos', titulo: 'Saldos' }
  ];
  contas: ContaBancaria[] = [];
  carregando = false;
  carregandoDetalhe = false;
  carregandoAssociados = false;
  salvando = false;
  busca = '';
  sucesso = '';
  erroModal: ErroModal | null = null;
  formularioAberto = false;
  pixModalAberto = false;
  criando = false;
  selecionada: ContaBancaria | null = null;
  abaAtiva: AbaConta = 'geral';
  valores: Record<string, string> = {};
  valoresOriginais: Record<string, string> = {};
  valoresRelacionados: Record<string, string> = {};
  categoriasExtrato: any[][] = [];
  faixasCep: any[][] = [];
  campoOrdenacao: ColunaOrdenacao = 'codigoInterno';
  ordemAscendente = true;
  paginaAtual = 1;
  itensPorPagina = 11;
  readonly opcoesPorPagina = [11, 25, 50, 100];
  dropdownAberto: string | null = null;
  dropdownPos: { top: number; right: number } | null = null;

  private readonly camposGerais = new Set([
    'CODCTABCOINT', 'CODCTABCO', 'DESCRICAO', 'DVC_OBSCOMEX', 'NUCONTRATO', 'CODAGEBENEF', 'CODCTABENEF',
    'CODCONTARURAL', 'NUMCONTARURAL', 'SEQREM2', 'VALORJUROS', 'VALORMULTA', 'DIASMULTA', 'DIASPROTESTO',
    'INSTRUCAOPROTESTO', 'INSTRUCAONEGATIVACAO', 'CONTABILIZARDIAS', 'DIASJURO', 'DIASPARANEGATIVACAO',
    'ORGAONEGATIVADOR', 'DTENVIOAPIBANCO', 'DTDESCREDCONTA', 'DESCONSLCDPR', 'AD_IMPRIMEORCAMENTO',
    'TITINFADICPIX', 'MENADICPIX', 'DATAJURO'
  ]);
  private readonly camposEdi = new Set(['PJBCHAVE', 'PJBCRED', 'PJBCONBAIXCRED', 'BJBBAIBOLPAG', 'LOGOURL']);
  private readonly formatadorMoeda = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  constructor(private readonly gateway: SankhyaGatewayService) {}

  ngOnInit(): void { this.carregar(); }

  get contasFiltradas(): ContaBancaria[] {
    const termo = this.normalizar(this.busca);
    const filtradas = this.contas.filter(conta => !termo || this.normalizar([
      conta.codigoInterno, conta.conta, conta.descricao, conta.codigoBanco, conta.banco,
      conta.codigoAgencia, conta.agencia, conta.codigoEmpresa, conta.empresa
    ].join(' ')).includes(termo));
    const direcao = this.ordemAscendente ? 1 : -1;
    return filtradas.sort((a, b) => {
      const left = a[this.campoOrdenacao] ?? '';
      const right = b[this.campoOrdenacao] ?? '';
      const comparacao = this.campoOrdenacao === 'codigoInterno' || this.campoOrdenacao === 'saldoReal'
        ? this.numero(left) - this.numero(right)
        : String(left).localeCompare(String(right), 'pt-BR', { numeric: true, sensitivity: 'base' });
      return comparacao * direcao;
    });
  }

  get camposAbaAtiva(): CampoContaMetadata[] {
    return this.metadados.filter(campo => this.abaDoCampo(campo) === this.abaAtiva);
  }

  get quantidadeContas(): number { return this.contas.length; }

  get totalPaginas(): number { return Math.max(1, Math.ceil(this.contasFiltradas.length / this.itensPorPagina)); }
  get contasPagina(): ContaBancaria[] {
    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    return this.contasFiltradas.slice(inicio, inicio + this.itensPorPagina);
  }
  get paginas(): number[] {
    const delta = 2;
    const paginas: number[] = [];
    for (let atual = Math.max(1, this.paginaAtual - delta); atual <= Math.min(this.totalPaginas, this.paginaAtual + delta); atual++) paginas.push(atual);
    return paginas;
  }

  get contasPixDisponiveis(): ContaBancaria[] {
    return this.contas
      .filter(conta => Boolean(conta.chavePix.trim()))
      .sort((a, b) => Number(this.ehSim(b.imprimeOrcamento)) - Number(this.ehSim(a.imprimeOrcamento)));
  }

  carregar(tentativasConcorrencia = 0, manterPaginaAtual = false): void {
    this.carregando = true;
    this.gateway.buscarContasNativo().subscribe({
      next: (resposta: any) => {
        if (!this.respostaSucesso(resposta)) {
          if (this.respostaConcorrencia(resposta) && tentativasConcorrencia < 3) {
            setTimeout(() => this.carregar(tentativasConcorrencia + 1, manterPaginaAtual), 300 * (tentativasConcorrencia + 1));
            return;
          }
          this.carregando = false;
          this.mostrarErro(resposta, 'O Sankhya não conseguiu carregar as contas.');
          return;
        }
        this.carregando = false;
        const body = resposta?.responseBody ?? resposta?.serviceResponse?.responseBody ?? resposta;
        this.contas = (Array.isArray(body?.result) ? body.result : []).map((linha: any[]) => this.mapearConta(linha));
        this.paginaAtual = manterPaginaAtual ? Math.min(this.paginaAtual, this.totalPaginas) : 1;
      },
      error: (falha: any) => {
        this.carregando = false;
        this.mostrarErro(falha, 'Falha ao consultar as contas.');
      }
    });
  }

  abrirNova(): void {
    this.fecharDropdown();
    this.selecionada = null;
    this.criando = true;
    this.valores = this.valoresPadrao();
    this.valoresOriginais = {};
    this.valoresRelacionados = {};
    this.categoriasExtrato = [];
    this.faixasCep = [];
    this.abaAtiva = 'geral';
    this.formularioAberto = true;
  }

  abrirPixModal(): void { this.pixModalAberto = true; }
  fecharPixModal(): void { this.pixModalAberto = false; }

  abrirEdicao(conta: ContaBancaria): void {
    this.fecharDropdown();
    if (this.carregandoDetalhe) return;
    this.selecionada = conta;
    this.criando = false;
    this.carregandoDetalhe = true;
    this.gateway.buscarDetalheContaNativo(conta.codigoInterno).subscribe({
      next: (resposta: any) => {
        this.carregandoDetalhe = false;
        if (!this.respostaSucesso(resposta)) {
          this.mostrarErro(resposta, 'O Sankhya não conseguiu carregar todos os dados da conta.');
          return;
        }
        const body = resposta?.responseBody ?? resposta?.serviceResponse?.responseBody ?? resposta;
        const linha = Array.isArray(body?.result) ? body.result[0] : null;
        const campos: string[] = Array.isArray(body?.fields) ? body.fields : [];
        if (!linha || !campos.length) {
          this.mostrarErro({ statusMessage: 'O Sankhya não retornou os dados completos desta conta.' }, 'Erro ao carregar conta');
          return;
        }
        this.valores = Object.fromEntries(this.metadados.map(campo => [campo.nome, String(linha[campos.indexOf(campo.nome)] ?? '')]));
        this.valoresOriginais = { ...this.valores };
        this.valoresRelacionados = Object.fromEntries(campos.map((campo, indice) => [campo, String(linha[indice] ?? '')]));
        this.abaAtiva = 'geral';
        this.formularioAberto = true;
        this.carregarDadosRelacionados(conta.codigoInterno);
      },
      error: (falha: any) => {
        this.carregandoDetalhe = false;
        this.mostrarErro(falha, 'Falha ao carregar todos os dados da conta.');
      }
    });
  }

  toggleDropdown(event: Event, codigo: string): void {
    event.stopPropagation();
    if (this.dropdownAberto === codigo) { this.fecharDropdown(); return; }
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const alturaMenu = 54;
    this.dropdownAberto = codigo;
    this.dropdownPos = {
      top: window.innerHeight - rect.bottom < alturaMenu + 12 ? Math.max(8, rect.top - alturaMenu - 6) : rect.bottom + 6,
      right: window.innerWidth - rect.right
    };
  }

  fecharDropdown(): void { this.dropdownAberto = null; this.dropdownPos = null; }

  @HostListener('document:click')
  fecharDropdownAoClicarFora(): void { this.fecharDropdown(); }

  fecharFormulario(): void {
    if (this.salvando) return;
    this.formularioAberto = false;
    this.selecionada = null;
  }

  selecionarAba(aba: AbaConta): void { this.abaAtiva = aba; }

  alterarBusca(): void { this.paginaAtual = 1; }
  ordenarPor(campo: ColunaOrdenacao): void {
    this.ordemAscendente = this.campoOrdenacao === campo ? !this.ordemAscendente : true;
    this.campoOrdenacao = campo;
    this.paginaAtual = 1;
  }

  irParaPagina(pagina: number): void { this.paginaAtual = Math.min(this.totalPaginas, Math.max(1, pagina)); }
  onItensPorPaginaChange(): void { this.paginaAtual = 1; }

  classeOrdenacao(campo: ColunaOrdenacao): string {
    if (this.campoOrdenacao !== campo) return '';
    return this.ordemAscendente ? 'ds-table-sort--asc' : 'ds-table-sort--desc';
  }

  sentidoOrdenacao(campo: ColunaOrdenacao): string {
    return this.campoOrdenacao !== campo ? 'none' : this.ordemAscendente ? 'ascending' : 'descending';
  }

  campoSomenteLeitura(campo: CampoContaMetadata): boolean {
    return campo.leitura || campo.nome === 'CODCTABCOINT';
  }

  descricaoRelacionada(campo: CampoContaMetadata): string {
    const relacionados: Record<string, string> = {
      CODBCO: 'Banco.NOMEBCO', CODAGE: 'AgenciaBancaria.NOMEAGE', CODEMP: 'Empresa.NOMEFANTASIA',
      CODCTACTB: 'PlanoContaEmpresa.DESCRCTA', CODPARC: 'Parceiro.NOMEPARC', CODCORRBCO: 'CorrespondenteBancario.NOMEBCO',
      CODMOEDA: 'Moeda.NOMEMOEDA', CODCTABCOINTREM: 'ContaDestino.DESCRICAO', CODLANCBAIXABOLRAP: 'HistoricoBancario.DESCRLANCBCO',
      CODTIPOPERBAIXABOLRAP: 'TipoOperacaoBaixa.DESCROPER', MODBOLETA: 'ModeloNotaFiscal.NOME',
      CTAMINBOLETA: 'ContaBancariaDespesa.DESCRICAO', NURFEMODCHEQG: 'Relatorio.DESCRICAO',
      CODCTABAIXA: 'ContaBancaria.DESCRICAO', NURFEMODBOLETO: 'ModeloPixCobranca.DESCRICAO'
    };
    return this.valoresRelacionados[relacionados[campo.nome]] ?? '';
  }

  exibirValor(campo: CampoContaMetadata): string {
    const valor = this.valores[campo.nome] ?? '';
    if (campo.opcoes.length) return campo.opcoes.find(opcao => opcao.valor === valor)?.label ?? valor;
    if (campo.tipo === 'D' && valor) return valor;
    if (campo.tipo === 'F' && valor) return this.exibirSaldo(valor);
    return valor;
  }

  exibirChavePix(valor: string | null | undefined): string {
    const texto = String(valor ?? '');
    if (!texto || texto.includes('@')) return texto;

    const digitos = texto.replace(/\D/g, '');
    const telefone = texto.startsWith('+55') && (digitos.length === 12 || digitos.length === 13)
      ? digitos.slice(2)
      : digitos;

    if (!texto.startsWith('+55') && this.cpfValido(digitos)) {
      return this.formatarCpf(digitos);
    }

    if (telefone.length === 10 || telefone.length === 11) {
      const ddd = telefone.slice(0, 2);
      const numero = telefone.slice(2);
      const separador = numero.length > 4 ? numero.length - 4 : numero.length;
      return `(${ddd}) ${numero.slice(0, separador)}${numero.length > 4 ? `-${numero.slice(separador)}` : ''}`;
    }

    if (digitos.length >= 12 && digitos.length <= 14) {
      return this.formatarCnpj(digitos);
    }

    return texto;
  }

  atualizarChavePix(valor: string): void {
    const texto = String(valor ?? '');
    if (texto.includes('@') || /[^\d+().\s/-]/.test(texto)) {
      this.valores['CHAVEPIX'] = texto;
      return;
    }

    const digitos = texto.replace(/\D/g, '');
    this.valores['CHAVEPIX'] = texto.trim().startsWith('+') && digitos.startsWith('55')
      ? `+${digitos}`
      : digitos;
  }

  private normalizarChavePixParaSalvar(valor: string): string {
    if (!valor || valor.includes('@') || /[^\d+().\s/-]/.test(valor)) return valor;

    const digitos = valor.replace(/\D/g, '');
    if (valor.trim().startsWith('+') && digitos.startsWith('55') && (digitos.length === 12 || digitos.length === 13)) {
      return `+${digitos}`;
    }
    if (digitos.length === 11 && this.cpfValido(digitos)) return digitos;
    if (digitos.length === 10 || digitos.length === 11) return `+55${digitos}`;
    if (digitos.length === 14) return digitos;
    return valor;
  }

  private formatarCnpj(digitos: string): string {
    const cnpj = digitos.slice(0, 14);
    return cnpj
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }

  private formatarCpf(digitos: string): string {
    return digitos.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }

  private cpfValido(valor: string): boolean {
    if (!/^\d{11}$/.test(valor) || /^(\d)\1{10}$/.test(valor)) return false;
    const calcularDigito = (tamanho: number): number => {
      let soma = 0;
      for (let i = 0; i < tamanho; i++) soma += Number(valor[i]) * (tamanho + 1 - i);
      const resto = soma % 11;
      return resto < 2 ? 0 : 11 - resto;
    };
    return calcularDigito(9) === Number(valor[9])
      && calcularDigito(10) === Number(valor[10]);
  }

  salvar(): void {
    if (this.salvando) return;
    const conta = this.valores['CODCTABCO']?.trim() ?? '';
    const banco = this.valores['CODBCO']?.trim() ?? '';
    const agencia = this.valores['CODAGE']?.trim() ?? '';
    if (!conta || !banco || !agencia) {
      this.mostrarErro({ message: 'Preencha Conta, Banco e Agência.' }, 'Dados obrigatórios');
      return;
    }

    const valores = Object.fromEntries(this.metadados
      .filter(campo => !this.campoSomenteLeitura(campo))
      .filter(campo => this.criando ? Boolean(this.valores[campo.nome]) : this.valores[campo.nome] !== this.valoresOriginais[campo.nome])
      .map(campo => [campo.nome, campo.nome === 'CHAVEPIX'
        ? this.normalizarChavePixParaSalvar(this.valores[campo.nome] ?? '')
        : this.valores[campo.nome] ?? '']));
    if (!this.criando && !Object.keys(valores).length) {
      this.mostrarErro({ message: 'Nenhuma alteração foi feita.' }, 'Nenhuma alteração');
      return;
    }

    this.salvando = true;
    const payload = { criar: this.criando, codigoInterno: this.selecionada?.codigoInterno, valores };
    const eraCriacao = this.criando;
    this.gateway.salvarContaNativo(payload).subscribe({
      next: (resposta: any) => {
        this.salvando = false;
        if (!this.respostaSucesso(resposta)) {
          this.mostrarErro(resposta, 'O Sankhya não confirmou o salvamento da conta.');
          return;
        }
        const codigoContaSalva = this.selecionada?.codigoInterno;
        if (codigoContaSalva && Object.prototype.hasOwnProperty.call(valores, 'CHAVEPIX')) {
          const chavePix = String(valores['CHAVEPIX'] ?? '');
          this.contas = this.contas.map(conta => conta.codigoInterno === codigoContaSalva
            ? { ...conta, chavePix }
            : conta);
        }
        this.fecharFormulario();
        this.sucesso = eraCriacao ? 'Conta cadastrada.' : 'Conta atualizada.';
        // Aguarda o listener do DatasetSP.save liberar a sessao antes de reler a lista.
        setTimeout(() => this.carregar(0, true), 300);
        setTimeout(() => this.sucesso = '', 3500);
      },
      error: (falha: any) => {
        this.salvando = false;
        this.mostrarErro(falha, 'Falha ao salvar a conta.');
      }
    });
  }

  fecharErro(): void { this.erroModal = null; }

  exibirSaldo(valor: string): string {
    const texto = String(valor ?? '');
    const normalizado = texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto;
    const numero = Number(normalizado);
    return Number.isFinite(numero) ? this.formatadorMoeda.format(numero) : valor || '—';
  }

  private carregarDadosRelacionados(codigoInterno: string): void {
    this.carregandoAssociados = true;
    let pendentes = 2;
    const concluido = (): void => { pendentes--; if (!pendentes) this.carregandoAssociados = false; };
    this.gateway.buscarCategoriasExtratoContaNativo(codigoInterno).subscribe({
      next: (resposta: any) => {
        if (!this.respostaSucesso(resposta)) this.mostrarErro(resposta, 'Erro ao consultar categorias do extrato.');
        else this.categoriasExtrato = this.linhasAssociadas(resposta);
        concluido();
      },
      error: falha => { this.mostrarErro(falha, 'Falha ao consultar categorias do extrato.'); concluido(); }
    });
    this.gateway.buscarFaixasCepContaNativo(codigoInterno).subscribe({
      next: (resposta: any) => {
        if (!this.respostaSucesso(resposta)) this.mostrarErro(resposta, 'Erro ao consultar os dados EDI.');
        else this.faixasCep = this.linhasAssociadas(resposta);
        concluido();
      },
      error: falha => { this.mostrarErro(falha, 'Falha ao consultar os dados EDI.'); concluido(); }
    });
  }

  private linhasAssociadas(resposta: any): any[][] {
    const body = resposta?.responseBody ?? resposta?.serviceResponse?.responseBody ?? resposta;
    return Array.isArray(body?.result) ? body.result : [];
  }

  private abaDoCampo(campo: CampoContaMetadata): AbaConta {
    if (campo.aba === 'Cadastros') return 'cadastros';
    if (campo.aba === 'Pix') return 'pix';
    if (this.camposGerais.has(campo.nome)) return 'geral';
    if (this.camposEdi.has(campo.nome)) return 'edi';
    return 'boletos';
  }

  private valoresPadrao(): Record<string, string> {
    return Object.fromEntries(this.metadados.map(campo => [campo.nome,
      campo.nome === 'ATIVA' ? 'S' : campo.nome === 'EXCLUSIVA' ? 'N' : campo.nome === 'CLASSE' ? 'C' : '']));
  }

  private respostaSucesso(resposta: any): boolean {
    return resposta?.status === undefined || String(resposta.status) === '1';
  }

  private respostaConcorrencia(resposta: any): boolean {
    const retorno = resposta?.error ?? resposta;
    return String(retorno?.status) === '4' && String(retorno?.statusMessage ?? '').toLowerCase().includes('concorr');
  }

  private mostrarErro(retorno: any, fallback: string): void {
    const resposta = retorno?.error ?? retorno;
    this.erroModal = {
      titulo: resposta?.status !== undefined || resposta?.tsError?.tsErrorCode ? 'Retorno do Sankhya' : fallback,
      mensagem: resposta?.statusMessage ?? resposta?.detail ?? resposta?.message ?? fallback,
      codigo: resposta?.tsError?.tsErrorCode ?? resposta?.errorCode
    };
  }

  private mapearConta(linha: any[]): ContaBancaria {
    const valor = (indice: number): string => String(linha?.[indice] ?? '');
    return {
      codigoInterno: valor(0), conta: valor(1), descricao: valor(2), codigoBanco: valor(3),
      banco: valor(4), codigoAgencia: valor(5), agencia: valor(6), codigoEmpresa: valor(7),
      empresa: valor(8), codigoPlanoContas: valor(9), planoContas: valor(10), tipoConta: valor(11),
      ativa: valor(12), saldoBanco: valor(16), saldoReal: valor(17), implantacao: valor(14),
      chavePix: valor(18), imprimeOrcamento: valor(19)
    };
  }

  private ehSim(valor: string): boolean {
    return ['S', 'SIM', 'Y', 'TRUE', '1'].includes(String(valor ?? '').trim().toUpperCase());
  }

  private numero(valor: string): number {
    const texto = String(valor ?? '');
    const normalizado = texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto;
    const numero = Number(normalizado);
    return Number.isFinite(numero) ? numero : 0;
  }

  private normalizar(valor: string): string {
    return String(valor ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR');
  }
}
